import { z } from 'zod'

/**
 * Project files (ADR 0017): files uploaded to a container as extra context (Markdown, PDF, images, …).
 * Text is extracted by the worker and indexed for search, chat, MCP and the live copilot.
 * Paths: GET/POST /api/v1/containers/:id/files, GET/DELETE /api/v1/files/:id, POST /api/v1/files/:id/retry.
 */

const id = z.uuid()
const isoDateTime = z.iso.datetime({ offset: true })

/** max. upload size per file */
export const MAX_FILE_BYTES = 25 * 1024 * 1024
/** max. files per upload request */
export const MAX_FILES_PER_UPLOAD = 20

/** pending = waiting for the worker · done = text extracted · failed = error (retry possible) ·
 * skipped = no text extraction (file type, AI policy) – only the filename is searchable */
export const FileExtractStatus = z.enum(['pending', 'done', 'failed', 'skipped'])
export type FileExtractStatus = z.infer<typeof FileExtractStatus>

export const FileExtractMethod = z.enum(['text', 'pdf', 'docx', 'xlsx', 'vision', 'none'])
export type FileExtractMethod = z.infer<typeof FileExtractMethod>

export const ProjectFile = z.object({
  id,
  containerId: id,
  filename: z.string(),
  mime: z.string(),
  size: z.number().int(),
  createdAt: isoDateTime,
  createdBy: z.object({ id, name: z.string() }).nullable(),
  extractStatus: FileExtractStatus,
  extractMethod: FileExtractMethod.nullable(),
  /** German, for the UI */
  extractError: z.string().nullable(),
  /** length of the extracted text */
  chars: z.number().int(),
  /** search index chunks (0 = not indexed yet or nothing to index) */
  chunks: z.number().int(),
  indexedAt: isoDateTime.nullable(),
  /** download/preview path (session auth) */
  url: z.string(),
})
export type ProjectFile = z.infer<typeof ProjectFile>

export const ProjectFileDetail = ProjectFile.extend({
  /** extracted text (Markdown), clipped for the preview */
  text: z.string(),
  truncated: z.boolean(),
})
export type ProjectFileDetail = z.infer<typeof ProjectFileDetail>

export const UploadFilesResult = z.object({
  files: z.array(ProjectFile),
  /** files that were rejected (too large, empty) – German message per file */
  rejected: z.array(z.object({ filename: z.string(), message: z.string() })),
})
export type UploadFilesResult = z.infer<typeof UploadFilesResult>
